import {computed, ref} from 'vue'
import _ from 'lodash'
import emitter from '@/services/infrastructure/emitter'
import {NEXT_ORG, NO_ORG_SELECTED, ORG_SELECTED, ORG_SWITCHED, PREV_ORG} from '@/types/events'
import {get, post} from '@/services/infrastructure/api.service'
import {useFirestoreService} from '@/services/infrastructure/firestore.service'
import {useFunctionService} from '@/services/infrastructure/function.service'
import {Tenant} from '@/tenants/Tenant'
import {TenantDto} from '@/tenants/TenantDto'
import {getUser} from '@/services/infrastructure/auth.service'
import hotkeys from 'hotkeys-js'
import {fetchPatronOverview} from '@/services/infrastructure/patron.service'
import '@/tenants/tenant-url-handler.service'
import {getEnvPrefix} from '@/services/infrastructure/envhelper.service'

const myOrgs = ref<Array<Tenant>>([])
// roles that have grants in Pracsys
const allowedRoles = ['tv', 'to', 'ta', 'own', 'pm']
const selectedOrg = ref<Tenant | null>(null)
const firestoreService = useFirestoreService()
const firebaseFunctions = useFunctionService()

const SESSION_ORG = 'selectedOrg'

const rememberOrgNo = (orgNo: string) => {
    window.localStorage.setItem(SESSION_ORG, orgNo)
    window.sessionStorage.setItem(SESSION_ORG, orgNo)
}

export const rememberTenantId = (tenantId: string) => {
    // example format: /organization/_347538809xxxxyyyyzzzz/Suppliers
    //format: _{orgId}{uuid} - orgId is 9 digits, uuid 12
    const orgId = (tenantId as string).substring(1, 10)
    rememberOrgNo(orgId)
}

const forgetOrgNo = async () => {
    window.localStorage.clear()
    window.sessionStorage.removeItem(SESSION_ORG)
}

const recallOrgNo = () => {
    const fromSession = window.sessionStorage.getItem(SESSION_ORG)
    const fromLocalStorage = window.localStorage.getItem(SESSION_ORG)
    const sessionOrg = fromSession || fromLocalStorage
    if (fromLocalStorage && !fromSession)
        rememberOrgNo(fromLocalStorage)
    return sessionOrg
}

/**
 * Explicit initialization for BootstrapService.
 * Called with the authenticated user's ID to load and select organizations.
 * Returns a Promise that resolves when orgs are loaded and optionally selected.
 */
const initUserOrgs = async (userId: string): Promise<void> => {
    // In mock mode, use injected org from Cypress
    // @ts-ignore
    if (window.useMockBackend && window.cyInitialSelectedOrg) {
        // @ts-ignore
        const mockOrg: Tenant = window.cyInitialSelectedOrg
        myOrgs.value = [mockOrg]
        selectOrg(mockOrg)
        console.log('[org.service] Using mock org from Cypress:', mockOrg.organizationName)
        return
    }

    myOrgs.value = await findOrgs(userId)

    const previouslySelectedOrgNo = recallOrgNo()
    if (previouslySelectedOrgNo) {
        const org = _.find(myOrgs.value, {organizationNumber: previouslySelectedOrgNo})
        if (org) {
            selectOrg(org)
        }
    }
    if (!hasSelectedOrg()) {
        emitter.emit(NO_ORG_SELECTED)
    }
}

/**
 * Legacy function for backward compatibility.
 * Delegates to initUserOrgs if user is available.
 */
const fetchUserOrgs = async () => {
    const user = getUser.value
    if (user?.uid) {
        await initUserOrgs(user.uid)
    } else {
        console.warn('[org.service] fetchUserOrgs called without authenticated user')
    }
}

const findOrgs = async (userId: string): Promise<Tenant[]> => {
    const firestoreOrgs = await firestoreService.findOrgs(userId)
    if (firestoreOrgs.length === 0) return Promise.resolve([])
    const provisionedTenants = await getTenants()
    return firestoreOrgs
        .map(org => {
            return {
                ...org,
                isProvisioned: _.some(provisionedTenants, {tenantId: org.organizationId}),
                vatLiable: true //TODO: Implement VAT liability
            }
        })
        // backend could also only return orgs where user has allowed roles
        // Though frontend could still show them with a request access button
        // I think it makes the most sense to make that choice in the frontend
        .filter(t => t.isProvisioned && t.roles?.some(role => allowedRoles.includes(role)))
}

const debouncedEmitOrgSelected = _.debounce(() => {
    emitter.emit(ORG_SELECTED)
}, 300, {leading: false, trailing: true})

const debouncedEmitOrgSwitched = _.debounce(() => {
    emitter.emit(ORG_SWITCHED)
}, 300, {leading: false, trailing: true})


const selectOrg = (org: Tenant) => {
    selectedOrg.value = org
    rememberOrgNo(org.organizationNumber)
    debouncedEmitOrgSelected()
    setTitleFromOrg(org)
}

const setTitleFromOrg = (org: Tenant) => {
    document.title = `${getEnvPrefix()}${org.organizationName} - Conta Pro`
}

const switchOrg = (org: Tenant) => {
    selectOrg(org)
    debouncedEmitOrgSwitched()
}

const getSelectedOrgId = () => selectedOrg?.value?.id
const getSelectedOrgNo = () => selectedOrg?.value?.organizationNumber

const hasSelectedOrg = () => {
    return selectedOrg.value !== null
}


// @ts-ignore
if (window.Cypress) {
    // @ts-ignore
    window.selectOrg = selectOrg
    // @ts-ignore
    window.myOrgs = myOrgs
}

const getTenants = async (): Promise<TenantDto[]> => {
    return await get<TenantDto[]>('/tenants') ?? []
}

const handleTenantProvision = async (orgNo: string, tenantId: string, businessName: string) => {
    let address = {
        postalCode: '1000',
        postalPlace: 'Test Sted',
        streetAddress: ['Testveien 1']
    }
    try {
        const patronData = await fetchPatronOverview(orgNo)
        address = {...patronData.address}
    } catch (e) {
        console.error('Error fetching patron data', e)
    }

    const provisionServiceCommand = {
        tenantId: tenantId,
        organizationNumber: orgNo,
        organizationName: businessName,
        organizationStructure: 'CORPORATION',
        address,
        dateOfEstablishment: new Date()
    }
    await post('/tenants/:provision', provisionServiceCommand)
    await fetchUserOrgs()
}

const provisionTenant = async (orgNo: string, tenantId: string, businessName: string) => {
    if (!confirm(`Du har ikke kjøpt regnskap for virksomhet ${businessName}. Ønsker du å kjøpe tilgang? Kun NOK 999/mnd!`)) {
        return
    }
    await handleTenantProvision(orgNo, tenantId, businessName)
}

const provisionTenantDirectly = async (orgNo: string, tenantId: string, businessName: string) => {
    await handleTenantProvision(orgNo, tenantId, businessName)
}

const registerOrganization = async (tenantId: string, orgNo: string, organizationName: string) => {
    const addRoleResult = await addRolesForNewOrganization(tenantId, orgNo, organizationName)
    if (!addRoleResult) {
        console.error(addRoleResult)
        console.error('Error adding role to organization')
        return false
    }
    return true
}

const addRolesForNewOrganization = async (tenantId: string, orgNo: string, organizationName: string) => {
    return firebaseFunctions.registerTenant({
        tenantId: tenantId,
        organizationName: organizationName,
        organizationNumber: orgNo
    })
}

const clearUserOrgs = () => {
    myOrgs.value = []
    selectedOrg.value = null
    forgetOrgNo()
        .catch((error) => console.error('Error forgetting org', error))
}

const orgSelectionComplete = new Promise((resolve) => {
    emitter.on(ORG_SELECTED, resolve)
    emitter.on(NO_ORG_SELECTED, resolve)
})

const sortedOrgs = computed(() => {
    return _.sortBy(myOrgs.value, 'organizationName')
})

const provisionedOrgs = computed(() => {
    return _.filter(sortedOrgs.value, 'isProvisioned')
})

const orgNumLengthIsValid = (orgNumLength: string) => {
    return orgNumLength.length === 9
}

const switchToPrevOrg = () => {
    const length = provisionedOrgs.value.length
    if (!length) {
        return
    }
    if (!selectedOrg.value) {
        switchOrg(provisionedOrgs.value[0])
        return
    }
    const currentIndex = provisionedOrgs.value.indexOf(selectedOrg.value)
    const newIndex = (currentIndex - 1 + length) % length
    switchOrg(provisionedOrgs.value[newIndex])
}

const switchToNextOrg = () => {
    const length = provisionedOrgs.value.length
    if (!length) {
        return
    }
    if (!selectedOrg.value) {
        switchOrg(provisionedOrgs.value[0])
        return
    }
    const currentIndex = provisionedOrgs.value.indexOf(selectedOrg.value)
    const newIndex = (currentIndex + 1) % length
    switchOrg(provisionedOrgs.value[newIndex])
}

emitter.on(PREV_ORG, switchToPrevOrg)
emitter.on(NEXT_ORG, switchToNextOrg)

hotkeys('ctrl+shift+up', switchToPrevOrg)
hotkeys('ctrl+shift+down', switchToNextOrg)


export {
    myOrgs,
    selectedOrg,
    getSelectedOrgId,
    getSelectedOrgNo,
    selectOrg,
    switchOrg,
    hasSelectedOrg,
    fetchUserOrgs,
    initUserOrgs,
    registerOrganization,
    addRolesForNewOrganization,
    clearUserOrgs,
    orgSelectionComplete,
    orgNumLengthIsValid,
    provisionTenant,
    provisionTenantDirectly
}
